import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSocial } from '../contexts/SocialContext';
import { useProject } from '../contexts/ProjectContext';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '../components/ui/alert';
import { 
  Calendar as CalendarIcon,
  Clock,
  Instagram,
  Facebook,
  Youtube,
  Plus,
  Trash2,
  Link2,
  Loader2,
  CheckCircle,
  XCircle,
  Send,
  ExternalLink,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { motion } from 'framer-motion';

const PLATFORMS = [
  { id: 'instagram', name: 'Instagram', icon: Instagram, color: 'text-pink-500' },
  { id: 'facebook', name: 'Facebook', icon: Facebook, color: 'text-blue-500' },
];

export default function SchedulerPage() {
  const [searchParams] = useSearchParams();
  const { api } = useAuth();
  const { accounts, scheduledPosts, fetchAccounts, disconnectAccount, fetchScheduledPosts, schedulePost, cancelPost, loading } = useSocial();
  const { projects, renders, fetchProjects } = useProject();
  
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  const [publishing, setPublishing] = useState({});
  const [metaStatus, setMetaStatus] = useState(null);

  // Schedule form state
  const [scheduleForm, setScheduleForm] = useState({
    render_id: '',
    platform: '',
    account_id: '',
    scheduled_time: new Date(),
    caption: '',
    hashtags: [],
    cutdown_key: null
  });
  const [hashtagInput, setHashtagInput] = useState('');

  // Check Meta OAuth configuration status
  useEffect(() => {
    const checkMetaStatus = async () => {
      try {
        const res = await api().get('/auth/meta/status');
        setMetaStatus(res.data);
      } catch (err) {
        setMetaStatus({ configured: false, message: 'Unable to check Meta configuration' });
      }
    };
    checkMetaStatus();
  }, [api]);

  // Handle OAuth callback params
  useEffect(() => {
    const success = searchParams.get('success');
    const error = searchParams.get('error');
    const accountsConnected = searchParams.get('accounts');
    
    if (success === 'true' && accountsConnected) {
      toast.success(`Connected: ${decodeURIComponent(accountsConnected)}`);
      fetchAccounts();
    } else if (error) {
      toast.error(`Connection failed: ${decodeURIComponent(error)}`);
    }
  }, [searchParams, fetchAccounts]);

  useEffect(() => {
    fetchAccounts();
    fetchScheduledPosts();
    fetchProjects();
  }, [fetchAccounts, fetchScheduledPosts, fetchProjects]);

  // Real Meta OAuth connect
  const handleConnect = async () => {
    if (!metaStatus?.configured) {
      toast.error('Meta OAuth not configured. Add META_APP_SECRET in Deployment Environment Variables.');
      return;
    }
    
    setConnecting(true);
    try {
      const res = await api().get('/auth/meta/login');
      const { auth_url } = res.data;
      
      if (auth_url) {
        // Redirect to Meta OAuth
        window.location.href = auth_url;
      } else {
        toast.error('Failed to get authorization URL');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to initiate OAuth');
      setConnecting(false);
    }
  };

  const handleDisconnect = async (accountId) => {
    try {
      await disconnectAccount(accountId);
      toast.success('Account disconnected');
    } catch (err) {
      toast.error('Failed to disconnect account');
    }
  };

  const handlePublishNow = async (postId) => {
    setPublishing(prev => ({ ...prev, [postId]: true }));
    try {
      await api().post(`/social/posts/${postId}/publish`);
      toast.success('Post published successfully!');
      fetchScheduledPosts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to publish');
    } finally {
      setPublishing(prev => ({ ...prev, [postId]: false }));
    }
  };

  const handleSchedule = async () => {
    if (!scheduleForm.render_id || !scheduleForm.account_id) {
      toast.error('Please fill in required fields');
      return;
    }

    setScheduling(true);
    try {
      await schedulePost({
        render_id: scheduleForm.render_id,
        platform: scheduleForm.platform,
        account_id: scheduleForm.account_id,
        scheduled_time: scheduleForm.scheduled_time.toISOString(),
        caption: scheduleForm.caption,
        hashtags: scheduleForm.hashtags,
        cutdown_key: scheduleForm.cutdown_key
      });
      
      toast.success('Post scheduled!');
      setScheduleDialogOpen(false);
      setScheduleForm({
        render_id: '',
        platform: '',
        account_id: '',
        scheduled_time: new Date(),
        caption: '',
        hashtags: [],
        cutdown_key: null
      });
    } catch (err) {
      toast.error('Failed to schedule post');
    } finally {
      setScheduling(false);
    }
  };

  const addHashtag = () => {
    if (hashtagInput && !scheduleForm.hashtags.includes(hashtagInput)) {
      setScheduleForm(prev => ({
        ...prev,
        hashtags: [...prev.hashtags, hashtagInput.startsWith('#') ? hashtagInput : `#${hashtagInput}`]
      }));
      setHashtagInput('');
    }
  };

  const removeHashtag = (tag) => {
    setScheduleForm(prev => ({
      ...prev,
      hashtags: prev.hashtags.filter(t => t !== tag)
    }));
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'scheduled':
        return <Badge className="bg-yellow-500/20 text-yellow-400">Scheduled</Badge>;
      case 'posted':
        return <Badge className="bg-green-500/20 text-green-400">Posted</Badge>;
      case 'failed':
        return <Badge className="bg-red-500/20 text-red-400">Failed</Badge>;
      default:
        return <Badge className="bg-zinc-500/20 text-zinc-400">{status}</Badge>;
    }
  };

  const getPlatformIcon = (platform) => {
    const platformConfig = PLATFORMS.find(p => p.id === platform);
    if (platformConfig) {
      const Icon = platformConfig.icon;
      return <Icon className={`w-5 h-5 ${platformConfig.color}`} />;
    }
    return null;
  };

  return (
    <Layout>
      <div className="p-8 max-w-6xl mx-auto" data-testid="scheduler-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-outfit text-3xl font-bold text-white mb-2">Social Scheduler</h1>
            <p className="text-zinc-400">Connect accounts and schedule your video posts</p>
          </div>
          <Button 
            onClick={() => setScheduleDialogOpen(true)}
            className="bg-primary text-primary-foreground hover:bg-primary/90 glow-primary"
            disabled={accounts.length === 0}
            data-testid="schedule-post-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Schedule Post
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Connected Accounts */}
          <div className="lg:col-span-1 space-y-4">
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader>
                <CardTitle className="text-white text-lg">Connected Accounts</CardTitle>
                <CardDescription className="text-zinc-400">
                  Link your Instagram & Facebook via Meta
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Configuration Status Alert */}
                {metaStatus && !metaStatus.configured && (
                  <Alert className="bg-yellow-500/10 border-yellow-500/30">
                    <AlertCircle className="h-4 w-4 text-yellow-500" />
                    <AlertTitle className="text-yellow-400">Setup Required</AlertTitle>
                    <AlertDescription className="text-yellow-300/80 text-sm">
                      To enable Instagram & Facebook posting, add these environment variables when deploying:
                      <ul className="mt-2 space-y-1 font-mono text-xs">
                        <li className={metaStatus.app_id_set ? 'text-green-400' : ''}>
                          {metaStatus.app_id_set ? '✓' : '•'} META_APP_ID
                        </li>
                        <li className={metaStatus.app_secret_set ? 'text-green-400' : ''}>
                          {metaStatus.app_secret_set ? '✓' : '•'} META_APP_SECRET
                        </li>
                      </ul>
                    </AlertDescription>
                  </Alert>
                )}

                {/* Meta OAuth Button */}
                <Button
                  onClick={handleConnect}
                  disabled={connecting || !metaStatus?.configured}
                  className={`w-full ${metaStatus?.configured 
                    ? 'bg-gradient-to-r from-pink-500 to-blue-500 text-white hover:opacity-90' 
                    : 'bg-zinc-700 text-zinc-400 cursor-not-allowed'}`}
                  data-testid="connect-meta-btn"
                >
                  {connecting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Connecting...
                    </>
                  ) : (
                    <>
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Connect Instagram & Facebook
                    </>
                  )}
                </Button>
                
                <p className="text-xs text-zinc-500 text-center">
                  Requires Instagram Business/Creator account connected to a Facebook Page
                </p>

                {/* Connected accounts list */}
                {accounts.length > 0 && (
                  <div className="mt-4 space-y-3">
                    <p className="text-sm text-zinc-400">Connected:</p>
                    {accounts.map((account) => {
                      const Icon = account.platform === 'instagram' ? Instagram : Facebook;
                      const color = account.platform === 'instagram' ? 'text-pink-500' : 'text-blue-500';
                      
                      return (
                        <div 
                          key={account.id}
                          className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg"
                        >
                          <div className="flex items-center gap-3">
                            <Icon className={`w-5 h-5 ${color}`} />
                            <div>
                              <p className="text-white text-sm font-medium">{account.account_name}</p>
                              <p className="text-zinc-500 text-xs capitalize">{account.platform}</p>
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDisconnect(account.id)}
                            className="text-zinc-400 hover:text-destructive"
                            data-testid={`disconnect-${account.id}`}
                          >
                            <XCircle className="w-4 h-4" />
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Deployment Instructions Card */}
            {metaStatus && !metaStatus.configured && (
              <Card className="bg-zinc-900/30 border-zinc-800 border-dashed">
                <CardHeader className="pb-2">
                  <CardTitle className="text-zinc-400 text-sm font-medium">Deployment Setup</CardTitle>
                </CardHeader>
                <CardContent className="text-xs text-zinc-500 space-y-2">
                  <p>1. Click <strong>Deploy</strong> (top right)</p>
                  <p>2. Add <strong>Environment Variables</strong>:</p>
                  <div className="bg-zinc-800/50 p-2 rounded font-mono text-[10px] leading-relaxed">
                    META_APP_ID=875437378589578<br/>
                    META_APP_SECRET=your_secret<br/>
                    META_OAUTH_REDIRECT_URI=https://your-app.emergent.sh/api/auth/meta/callback
                  </div>
                  <p>3. Add redirect URI to Meta App's <strong>Valid OAuth Redirect URIs</strong></p>
                  <p>4. Deploy to enable OAuth</p>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Scheduled Posts */}
          <div className="lg:col-span-2">
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader>
                <CardTitle className="text-white text-lg">Scheduled Posts</CardTitle>
                <CardDescription className="text-zinc-400">
                  Upcoming and past posts
                </CardDescription>
              </CardHeader>
              <CardContent>
                {scheduledPosts.length === 0 ? (
                  <div className="text-center py-12 text-zinc-500">
                    <CalendarIcon className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No scheduled posts yet</p>
                    <p className="text-sm mt-1">Connect an account and schedule your first post</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {scheduledPosts.map((post, index) => (
                      <motion.div
                        key={post.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                        className="flex items-start gap-4 p-4 bg-zinc-800/50 rounded-lg"
                        data-testid={`scheduled-post-${post.id}`}
                      >
                        <div className="flex-shrink-0">
                          {getPlatformIcon(post.platform)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            {getStatusBadge(post.status)}
                            <span className="text-xs text-zinc-500 font-mono">
                              {format(new Date(post.scheduled_time), 'MMM d, yyyy h:mm a')}
                            </span>
                          </div>
                          <p className="text-zinc-300 text-sm line-clamp-2">{post.caption || 'No caption'}</p>
                          {post.hashtags.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {post.hashtags.slice(0, 3).map(tag => (
                                <span key={tag} className="text-xs text-primary">{tag}</span>
                              ))}
                              {post.hashtags.length > 3 && (
                                <span className="text-xs text-zinc-500">+{post.hashtags.length - 3} more</span>
                              )}
                            </div>
                          )}
                        </div>
                        {post.status === 'scheduled' && (
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handlePublishNow(post.id)}
                              disabled={publishing[post.id]}
                              className="border-primary text-primary hover:bg-primary/10"
                              data-testid={`publish-now-${post.id}`}
                            >
                              {publishing[post.id] ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <>
                                  <Send className="w-4 h-4 mr-1" />
                                  Publish Now
                                </>
                              )}
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => cancelPost(post.id)}
                              className="text-zinc-500 hover:text-destructive"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </motion.div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Schedule Post Dialog */}
        <Dialog open={scheduleDialogOpen} onOpenChange={setScheduleDialogOpen}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-white">Schedule Post</DialogTitle>
              <DialogDescription className="text-zinc-400">
                Choose a video and schedule for posting
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label className="text-zinc-300">Platform & Account</Label>
                <Select
                  value={scheduleForm.account_id}
                  onValueChange={(v) => {
                    const account = accounts.find(a => a.id === v);
                    setScheduleForm(prev => ({
                      ...prev,
                      account_id: v,
                      platform: account?.platform || ''
                    }));
                  }}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700">
                    <SelectValue placeholder="Select account" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    {accounts.map(account => (
                      <SelectItem key={account.id} value={account.id} className="text-zinc-300">
                        <div className="flex items-center gap-2">
                          {getPlatformIcon(account.platform)}
                          <span>{account.account_name}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-zinc-300">Video</Label>
                <Select
                  value={scheduleForm.render_id}
                  onValueChange={(v) => setScheduleForm(prev => ({ ...prev, render_id: v }))}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700">
                    <SelectValue placeholder="Select a rendered video" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    {projects.map(project => (
                      <SelectItem key={project.id} value={project.id} className="text-zinc-300">
                        {project.title} - {project.artist_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-zinc-300">Schedule Date & Time</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left bg-zinc-800 border-zinc-700 text-zinc-300"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {format(scheduleForm.scheduled_time, 'PPP p')}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-zinc-900 border-zinc-800">
                    <Calendar
                      mode="single"
                      selected={scheduleForm.scheduled_time}
                      onSelect={(date) => date && setScheduleForm(prev => ({ ...prev, scheduled_time: date }))}
                      initialFocus
                    />
                    <div className="p-3 border-t border-zinc-800">
                      <Input
                        type="time"
                        value={format(scheduleForm.scheduled_time, 'HH:mm')}
                        onChange={(e) => {
                          const [hours, minutes] = e.target.value.split(':');
                          const newDate = new Date(scheduleForm.scheduled_time);
                          newDate.setHours(parseInt(hours), parseInt(minutes));
                          setScheduleForm(prev => ({ ...prev, scheduled_time: newDate }));
                        }}
                        className="bg-zinc-800 border-zinc-700 text-white"
                      />
                    </div>
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-2">
                <Label className="text-zinc-300">Caption</Label>
                <Textarea
                  value={scheduleForm.caption}
                  onChange={(e) => setScheduleForm(prev => ({ ...prev, caption: e.target.value }))}
                  placeholder="Write your caption..."
                  className="bg-zinc-800 border-zinc-700 text-white min-h-[100px]"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-zinc-300">Hashtags</Label>
                <div className="flex gap-2">
                  <Input
                    value={hashtagInput}
                    onChange={(e) => setHashtagInput(e.target.value)}
                    placeholder="Add hashtag"
                    className="bg-zinc-800 border-zinc-700 text-white"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addHashtag())}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={addHashtag}
                    className="border-zinc-700"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                {scheduleForm.hashtags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {scheduleForm.hashtags.map(tag => (
                      <Badge 
                        key={tag}
                        variant="secondary"
                        className="bg-zinc-800 text-primary cursor-pointer hover:bg-zinc-700"
                        onClick={() => removeHashtag(tag)}
                      >
                        {tag} ×
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setScheduleDialogOpen(false)}
                className="border-zinc-700 text-zinc-300"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSchedule}
                disabled={scheduling}
                className="bg-primary text-primary-foreground"
                data-testid="confirm-schedule-btn"
              >
                {scheduling ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Scheduling...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Schedule Post
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
